import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router-dom";
import { AuthLayout } from "./AuthLayout";
import { Button, TextField, Callout } from "@/design/components";
import { forgotPassword } from "@/api/auth";
import authStyles from "./AuthForm.module.css";

const schema = z.object({ email: z.string().trim().email("Enter a valid email") });
type Values = z.infer<typeof schema>;

/**
 * Request a password-reset link. Enumeration-safe: a successful submit ALWAYS shows
 * the same generic "if that email has an account, it's on its way" message — the API
 * never reveals whether the address exists, so neither does the UI.
 */
export function ForgotPasswordScreen() {
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const sentRef = useRef<HTMLDivElement>(null);
  // Move focus to the confirmation when it appears so AT reliably announces it
  // (a role=status region mounted already-populated is announced inconsistently).
  useEffect(() => {
    if (sent) sentRef.current?.focus();
  }, [sent]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { email: "" } });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await forgotPassword(values.email);
      setSent(true); // generic success — independent of whether the account exists
    } catch {
      // Only a transport/server failure lands here (the happy path is always ok:true).
      setFormError("Something went wrong sending the reset link. Please try again.");
    }
  });

  return (
    <AuthLayout>
      <header className={authStyles.head}>
        <p className="u-eyebrow">Account recovery</p>
        <h2 className={authStyles.title}>{sent ? "Check your inbox" : "Reset your password"}</h2>
      </header>

      {sent ? (
        <div ref={sentRef} tabIndex={-1} className={authStyles.focusResult}>
          <Callout tone="success" title="If that email has an account, a reset link is on its way">
            We've emailed a password-reset link to that address if it belongs to a Cogent account. The
            link is short-lived — check your spam folder if it doesn't arrive shortly.
          </Callout>
          <p className={authStyles.alt}>
            <Link to="/login">Back to sign in</Link>
          </p>
        </div>
      ) : (
        <>
          <form className={authStyles.form} onSubmit={onSubmit} noValidate>
            {formError && (
              <div className={authStyles.formError} role="alert">
                {formError}
              </div>
            )}
            <TextField
              label="Work email"
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              required
              error={errors.email?.message}
              {...register("email")}
            />
            <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
              Send reset link
            </Button>
          </form>
          <p className={authStyles.alt}>
            Remembered it? <Link to="/login">Sign in</Link>
          </p>
        </>
      )}
    </AuthLayout>
  );
}
